import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  Card,
  Title,
  Metric,
  Text,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
} from '@tremor/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from 'recharts';
import kpiAPI from '../../api/kpi';
import { computePowyzej24hKltGlt, getPowyzej24hFullRows } from '../../utils/kpiPowyzej24h';
import logoMcg from '../../assets/logo.png';
import logoMan from '../../assets/man białe.png';
import './KPIDashboard.css';
import './KPIStatPage.css';

const PROJECT = 'MAN';

/** Etykieta wartości na wykresie – kompaktowa „pigułka” z liczbą */
function ChartValueLabel({ x, y, value, payload, dataKey, color }) {
  const val = value ?? payload?.[dataKey];
  if (val == null || val === '') return null;
  const str = String(Math.round(Number(val)));
  const fill = color || '#f1f5f9';
  const w = Math.max(32, str.length * 11);
  const h = 26;
  return (
    <g transform={`translate(${x - w / 2},${y - h - 6})`}>
      <rect width={w} height={h} rx="13" ry="13" fill="rgba(30,41,59,0.92)" stroke={fill} strokeWidth="1.2" />
      <text x={w / 2} y={h / 2 + 1} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize="14" fontWeight="600">
        {str}
      </text>
    </g>
  );
}

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function formatDateForInput(d) {
  if (!d || !d.getTime) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** YYYY-MM-DD → DD-MM-YYYY dla wyświetlania */
function formatDateDisplay(yyyymmdd) {
  if (!yyyymmdd || typeof yyyymmdd !== 'string') return yyyymmdd || '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(yyyymmdd);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : yyyymmdd;
}

function parseDateOnly(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Dla arkusza PS: DATA WPISU może mieć czas – wyciągamy tylko datę. */
function parseDateFromCell(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getColumnIndex(headers, name) {
  const needle = String(name || '').trim().toLowerCase();
  const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
  return idx >= 0 ? idx : -1;
}

function getDateColumnIndex(headers) {
  const names = headers.map((h) => String(h || '').trim().toUpperCase());
  const idx = names.findIndex(
    (n) => n === 'DATA WPISU' || n.includes('DATA WPISU') || n === 'DATA' || n === 'DATE' || n.startsWith('DATA')
  );
  return idx >= 0 ? idx : 0;
}

/** Filtruje wiersze PS do jednego dnia (dateStr YYYY-MM-DD). */
function filterPsRowsByDate(rows, headers, dateStr) {
  if (!rows?.length || !dateStr) return [];
  const dateCol = getDateColumnIndex(headers);
  const [y, m, day] = dateStr.split('-').map(Number);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === y && d.getMonth() === m - 1 && d.getDate() === day;
  });
}

function parseNumber(val) {
  if (val == null || val === '') return 0;
  const v = typeof val === 'object' && val !== null && 'value' in val ? val.value : val;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function dateToStr(d) {
  return d ? formatDateForInput(d) : '';
}

/** Jedna agregacja Raport: byDay[dateStr] = { bhp, bhpHasEvent, pick, pickA, pickB, shipSq, shipRep, shipLergut, ..., repPick, repPickA, repPickB, inbound }. */
function aggregateRaportByDay(rows, headers) {
  const dataCol = getColumnIndex(headers, 'DATA');
  const zmianaCol = getColumnIndex(headers, 'ZMIANA');
  if (dataCol < 0) return {};
  const bhpCol = getColumnIndex(headers, 'BHP');
  const pickCol = getColumnIndex(headers, 'ilość pick');
  const shipSqCol = getColumnIndex(headers, 'SHIP SQ');
  const shipRepCol = getColumnIndex(headers, 'SHIP REP');
  const shipLergutCol = getColumnIndex(headers, 'SHIP LERGUT');
  const repPickCol = getColumnIndex(headers, 'REP PICK');
  const inboundCol = getColumnIndex(headers, 'INBOUND');
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    if (!byDay[dateStr]) {
      byDay[dateStr] = {
        bhp: 0, bhpHasEvent: false, pick: 0, pickA: 0, pickB: 0,
        shipSq: 0, shipRep: 0, shipLergut: 0,
        shipSqA: 0, shipSqB: 0, shipRepA: 0, shipRepB: 0, shipLergutA: 0, shipLergutB: 0,
        repPick: 0, repPickA: 0, repPickB: 0,
        inbound: 0, inboundA: 0, inboundB: 0,
      };
    }
    if (bhpCol >= 0) {
      const bv = parseNumber(row[bhpCol]);
      byDay[dateStr].bhp += bv;
      if (bv >= 1) byDay[dateStr].bhpHasEvent = true;
    }
    const z = zmianaCol >= 0
      ? String((row[zmianaCol] && typeof row[zmianaCol] === 'object' ? row[zmianaCol].value : row[zmianaCol]) ?? '').trim().toUpperCase()
      : '';
    const isB = z === 'B';
    if (pickCol >= 0) {
      const v = parseNumber(row[pickCol]);
      byDay[dateStr].pick += v;
      if (isB) byDay[dateStr].pickB += v;
      else byDay[dateStr].pickA += v;
    }
    const sq = shipSqCol >= 0 ? parseNumber(row[shipSqCol]) : 0;
    const rep = shipRepCol >= 0 ? parseNumber(row[shipRepCol]) : 0;
    const lerg = shipLergutCol >= 0 ? parseNumber(row[shipLergutCol]) : 0;
    byDay[dateStr].shipSq += sq;
    byDay[dateStr].shipRep += rep;
    byDay[dateStr].shipLergut += lerg;
    if (isB) {
      byDay[dateStr].shipSqB += sq;
      byDay[dateStr].shipRepB += rep;
      byDay[dateStr].shipLergutB += lerg;
    } else {
      byDay[dateStr].shipSqA += sq;
      byDay[dateStr].shipRepA += rep;
      byDay[dateStr].shipLergutA += lerg;
    }
    if (repPickCol >= 0) {
      const rv = parseNumber(row[repPickCol]);
      byDay[dateStr].repPick += rv;
      if (isB) byDay[dateStr].repPickB += rv;
      else byDay[dateStr].repPickA += rv;
    }
    if (inboundCol >= 0) {
      const inv = parseNumber(row[inboundCol]);
      byDay[dateStr].inbound += inv;
      if (isB) byDay[dateStr].inboundB += inv;
      else byDay[dateStr].inboundA += inv;
    }
  });
  return byDay;
}

/** Ilość dni od ostatniego dnia z PS > 0 (arkusz PS). Zwraca liczbę lub null. */
function getDaysSinceLastPs(byDayPs, displayDateStr) {
  const dates = Object.keys(byDayPs || {}).filter((d) => d <= (displayDateStr || '')).sort();
  if (!dates.length || !displayDateStr) return null;
  let lastPsDate = null;
  for (let i = dates.length - 1; i >= 0; i--) {
    const val = byDayPs[dates[i]];
    if (val != null && Number(val) > 0) {
      lastPsDate = dates[i];
      break;
    }
  }
  if (lastPsDate == null) return null;
  const a = new Date(lastPsDate + 'T12:00:00');
  const b = new Date(displayDateStr + 'T12:00:00');
  const diff = Math.round((b - a) / (24 * 60 * 60 * 1000));
  return diff >= 0 ? diff : null;
}

/** Ilość dni od ostatniego zdarzenia BHP (ostatnia „1” w kolumnie BHP). Zwraca liczbę lub null. */
function getDaysSinceLastBhpEvent(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).sort();
  if (!dates.length || !displayDateStr) return null;
  let lastEventDate = null;
  for (let i = dates.length - 1; i >= 0; i--) {
    const day = byDayRaport[dates[i]];
    if (day && day.bhpHasEvent) {
      lastEventDate = dates[i];
      break;
    }
  }
  if (lastEventDate == null) return null;
  const a = new Date(lastEventDate + 'T12:00:00');
  const b = new Date(displayDateStr + 'T12:00:00');
  const diff = Math.round((b - a) / (24 * 60 * 60 * 1000));
  return diff >= 0 ? diff : null;
}

/** Średnie z ostatnich N dni (pickA, pickB, repPickA, repPickB). */
function getAveragesFromRaport(byDayRaport, displayDateStr, lastN = 30) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-lastN);
  if (!slice.length) return { pickAvgA: null, pickAvgB: null, owwAvgA: null, owwAvgB: null };
  let sumA = 0, sumB = 0, sumOwwA = 0, sumOwwB = 0;
  slice.forEach((d) => {
    const r = byDayRaport[d];
    if (r) {
      sumA += r.pickA || 0;
      sumB += r.pickB || 0;
      sumOwwA += r.repPickA || 0;
      sumOwwB += r.repPickB || 0;
    }
  });
  const n = slice.length;
  return {
    pickAvgA: n ? Math.round((sumA / n) * 100) / 100 : null,
    pickAvgB: n ? Math.round((sumB / n) * 100) / 100 : null,
    owwAvgA: n ? Math.round((sumOwwA / n) * 100) / 100 : null,
    owwAvgB: n ? Math.round((sumOwwB / n) * 100) / 100 : null,
  };
}

/** PS: byDay[dateStr] = suma ILOŚĆ. */
function aggregatePsByDay(rows, headers) {
  const dateCol = getDateColumnIndex(headers);
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (iloscCol < 0) return {};
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    byDay[dateStr] = (byDay[dateStr] || 0) + parseNumber(row[iloscCol]);
  });
  return byDay;
}

function getPrevDayStr(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return formatDateForInput(d);
}

function getDateStrOffset(dateStr, daysOffset) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + daysOffset);
  return formatDateForInput(d);
}

function computeDodPct(lastVal, prevVal) {
  if (prevVal == null || prevVal === 0) return lastVal > 0 ? '100' : '0';
  return (((lastVal - prevVal) / prevVal) * 100).toFixed(1);
}

/** Wiersze PS w zakresie dat [fromStr, toStr] (włącznie). */
function filterPsRowsByDateRange(rows, headers, fromStr, toStr) {
  if (!rows?.length || !fromStr || !toStr) return [];
  const dateCol = getDateColumnIndex(headers);
  const from = new Date(fromStr + 'T00:00:00');
  const to = new Date(toStr + 'T23:59:59');
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    const t = d.getTime();
    return t >= from.getTime() && t <= to.getTime();
  });
}

/** Trend PICK + OWW – ostatnie 14 dni (dwie serie) dla wykresu. */
function getTrendPickOww14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      pick: (day && day.pick) || 0,
      oww: (day && day.repPick) || 0,
    };
  });
}

/** Trend 14 dni: SQ, REP, LERGUT (wartości dzienne) + suma. */
function getTransportTrend14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    const sq = (day && day.shipSq) ?? 0;
    const rep = (day && day.shipRep) ?? 0;
    const lergut = (day && day.shipLergut) ?? 0;
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      sq,
      rep,
      lergut,
      razem: sq + rep + lergut,
    };
  });
}

/** Trend 14 dni: INBOUND (ZMAGAZYNOWANE) – wartości dzienne do wykresu słupkowego. */
function getInboundTrend14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      inbound: (day && day.inbound) ?? 0,
    };
  });
}

/** Trend 14 dni: PS – wartości dzienne z byDayPs (suma ILOŚĆ). */
function getPsTrend14(byDayPs, displayDateStr) {
  const dates = Object.keys(byDayPs || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => ({
    dateStr: d,
    label: d.slice(8) + '.' + d.slice(5, 7),
    value: byDayPs[d] ?? 0,
  }));
}

/** Trend 14 dni: BHP – zdarzenia (bhp) na dzień do wykresu słupkowego. */
function getBhpTrend14(byDayRaport, displayDateStr) {
  const dates = Object.keys(byDayRaport || {}).filter((d) => d <= (displayDateStr || '')).sort();
  const slice = dates.slice(-14);
  return slice.map((d) => {
    const day = byDayRaport[d];
    const bhp = (day && (day.bhp ?? (day.bhpHasEvent ? 1 : 0))) || 0;
    return {
      dateStr: d,
      label: d.slice(8) + '.' + d.slice(5, 7),
      bhp,
    };
  });
}

/** Top 3 PSA po ILOŚĆ z podanych wierszy PS. */
function getTop3PsaFromRows(rows, headers) {
  const psaCol = getColumnIndex(headers, 'PSA');
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (psaCol < 0 || iloscCol < 0) return [];
  const byPsa = new Map();
  rows.forEach((row) => {
    const psa = String((row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol]) ?? '').trim() || '(pusty)';
    byPsa.set(psa, (byPsa.get(psa) || 0) + parseNumber(row[iloscCol]));
  });
  return Array.from(byPsa.entries())
    .map(([PSA, ILOŚĆ]) => ({ PSA, ILOŚĆ }))
    .sort((a, b) => b.ILOŚĆ - a.ILOŚĆ)
    .slice(0, 3);
}

/** Najczęstszy kod z kolumny "Opis Kodowania" lub "Kod" z podanych wierszy. */
function getMostFrequentCodeFromRows(rows, headers) {
  const col = getColumnIndex(headers, 'Opis Kodowania') >= 0 ? getColumnIndex(headers, 'Opis Kodowania') : getColumnIndex(headers, 'Kod');
  if (col < 0) return null;
  const byCode = new Map();
  rows.forEach((row) => {
    const code = String((row[col] && typeof row[col] === 'object' ? row[col].value : row[col]) ?? '').trim() || '(pusty)';
    byCode.set(code, (byCode.get(code) || 0) + 1);
  });
  const sorted = Array.from(byCode.entries()).sort((a, b) => b[1] - a[1]);
  return sorted.length ? sorted[0][0] : null;
}

/** Status kropki: green | yellow | red | gray. */
function getStatusDot(kpi, value, summary) {
  if (kpi === 'bhp') {
    if (value == null) return 'gray';
    const events = Number(value) || 0;
    if (events === 0) return 'green';
    if (events === 1) return 'yellow';
    return 'red';
  }
  if (kpi === 'ps') {
    if (value == null && (!summary?.psTableRows || summary.psTableRows === 0)) return 'gray';
    const count = Number(value) || 0;
    if (count === 0) return 'green';
    return 'yellow';
  }
  if (kpi === 'transport') return 'blue';
  if (kpi === 'powyzej24h') {
    if (value == null) return 'gray';
    const n = Number(value) || 0;
    if (n === 0) return 'green';
    return n <= 50 ? 'yellow' : 'red';
  }
  if (kpi === 'najstarszy') {
    if (value == null) return 'gray';
    const d = Number(value) || 0;
    if (d <= 2) return 'green';
    if (d === 3) return 'yellow';
    return 'red';
  }
  if (kpi === 'nieprzyjete') {
    if (value == null) return 'gray';
    const n = Number(value) || 0;
    if (n === 0) return 'green';
    return n <= 50 ? 'yellow' : 'red';
  }
  if (kpi === 'inspekt') {
    if (value == null) return 'gray';
    return 'green';
  }
  if (kpi === 'pick' || kpi === 'oww' || kpi === 'inbound') return 'green';
  return 'gray';
}

function KPIDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayDate, setDisplayDate] = useState(null);
  const [summary, setSummary] = useState({
    bhp: null,
    daysSinceBhp: null,
    pick: null,
    pickA: null,
    pickB: null,
    pickHourlyA: null,
    pickHourlyB: null,
    repPick: null,
    repPickA: null,
    repPickB: null,
    owwHourlyA: null,
    owwHourlyB: null,
    shipSqA: null,
    shipSqB: null,
    shipRepA: null,
    shipRepB: null,
    shipLergutA: null,
    shipLergutB: null,
    psSum: null,
    daysSincePs: null,
    inbound: null,
    inboundA: null,
    inboundB: null,
    inboundHourlyA: null,
    inboundHourlyB: null,
    powyzej24hRazem: null,
    powyzej24hKlt: null,
    powyzej24hGlt: null,
    najstarszaNieprzyjetaDni: null,
    nieprzyjete902: null,
    nieprzyjete923: null,
    inspektCount: null,
  });
  const [psTable, setPsTable] = useState({ headers: [], rows: [] });
  const [lastRefresh, setLastRefresh] = useState(null);
  const [trendPick14, setTrendPick14] = useState([]);
  const [transportTrend14, setTransportTrend14] = useState([]);
  const [inboundTrend14, setInboundTrend14] = useState([]);
  const [insights, setInsights] = useState({
    dodPct: null,
    bestShiftName: null,
    bestShiftHourly: null,
    daysSinceBhp: null,
    top3Psa: [],
    mostFrequentCode: null,
    alertCount: null,
  });
  const [availableDates, setAvailableDates] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKpi, setDrawerKpi] = useState(null);
  const dashboardDataRef = useRef({ byDayRaport: {}, byDayPs: {}, psRows: [], psHeaders: [], raportHeaders: [] });
  const printscreenRef = useRef(null);
  const [printscreenBusy, setPrintscreenBusy] = useState(false);
  const chartPanelRef = useRef(null);

  const openDrawer = useCallback((kpi) => {
    setDrawerKpi(kpi);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerKpi(null);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onEsc = (e) => e.key === 'Escape' && closeDrawer();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (drawerOpen && chartPanelRef.current) {
      chartPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [drawerOpen, drawerKpi]);

  const handlePrintscreen = useCallback(async () => {
    const el = printscreenRef.current;
    if (!el || printscreenBusy) return;
    setPrintscreenBusy(true);
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#1a1f2e',
      });
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });
      const a4W = 297;
      const a4H = 210;
      const imgW = canvas.width;
      const imgH = canvas.height;
      const ratio = imgW / imgH;
      const fitW = ratio > a4W / a4H ? a4W : a4H * ratio;
      const fitH = ratio > a4W / a4H ? a4W / ratio : a4H;
      const offsetX = (a4W - fitW) / 2;
      const offsetY = (a4H - fitH) / 2;
      pdf.setFillColor(26, 31, 46);
      pdf.rect(0, 0, a4W, a4H, 'F');
      pdf.addImage(imgData, 'PNG', offsetX, offsetY, fitW, fitH);
      const fileName = `Dashboard_KPI_${displayDate || new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('Błąd zapisu PDF:', err);
    } finally {
      setPrintscreenBusy(false);
    }
  }, [displayDate, printscreenBusy]);

  const applyDisplayDate = useCallback((displayDateStr, byDayRaport, byDayPs, psRows, psHeaders) => {
    const HOURS_PER_SHIFT = 7;
    if (!displayDateStr) {
      const p24 = dashboardDataRef.current?.powyzej24h;
      const nd = (() => { const d = p24?.najstarszaData; if (!d) return null; const dt = d instanceof Date ? d : new Date(d); return Math.floor((Date.now() - dt.getTime()) / 86400000); })();
      setSummary({ bhp: null, daysSinceBhp: null, pick: null, pickA: null, pickB: null, pickHourlyA: null, pickHourlyB: null, repPick: null, repPickA: null, repPickB: null, owwHourlyA: null, owwHourlyB: null, shipSqA: null, shipSqB: null, shipRepA: null, shipRepB: null, shipLergutA: null, shipLergutB: null, psSum: null, daysSincePs: null, inbound: null, inboundA: null, inboundB: null, inboundHourlyA: null, inboundHourlyB: null, powyzej24hRazem: p24?.razem ?? null, powyzej24hKlt: p24?.klt ?? null, powyzej24hGlt: p24?.glt ?? null, najstarszaNieprzyjetaDni: nd, nieprzyjete902: p24?.mag902 ?? null, nieprzyjete923: p24?.mag923 ?? null, inspektCount: null });
      setPsTable({ headers: [], rows: [] });
      setTrendPick14([]);
      setTransportTrend14([]);
      setInboundTrend14([]);
      setInsights({ dodPct: null, bestShiftName: null, bestShiftHourly: null, daysSinceBhp: null, top3Psa: [], mostFrequentCode: null, alertCount: null });
      return;
    }
    const r = byDayRaport[displayDateStr] || {};
    const hasRaport = Object.keys(byDayRaport).length > 0;
    setSummary({
      bhp: hasRaport ? (r.bhp ?? null) : null,
      daysSinceBhp: hasRaport ? getDaysSinceLastBhpEvent(byDayRaport, displayDateStr) : null,
      pick: hasRaport ? (r.pick ?? null) : null,
      pickA: hasRaport ? (r.pickA ?? null) : null,
      pickB: hasRaport ? (r.pickB ?? null) : null,
      pickHourlyA: r.pickA != null ? Math.round((r.pickA / HOURS_PER_SHIFT) * 100) / 100 : null,
      pickHourlyB: r.pickB != null ? Math.round((r.pickB / HOURS_PER_SHIFT) * 100) / 100 : null,
      repPick: hasRaport ? (r.repPick ?? null) : null,
      repPickA: hasRaport ? (r.repPickA ?? null) : null,
      repPickB: hasRaport ? (r.repPickB ?? null) : null,
      owwHourlyA: r.repPickA != null ? Math.round((r.repPickA / HOURS_PER_SHIFT) * 100) / 100 : null,
      owwHourlyB: r.repPickB != null ? Math.round((r.repPickB / HOURS_PER_SHIFT) * 100) / 100 : null,
      shipSqA: hasRaport ? (r.shipSqA ?? null) : null,
      shipSqB: hasRaport ? (r.shipSqB ?? null) : null,
      shipRepA: hasRaport ? (r.shipRepA ?? null) : null,
      shipRepB: hasRaport ? (r.shipRepB ?? null) : null,
      shipLergutA: hasRaport ? (r.shipLergutA ?? null) : null,
      shipLergutB: hasRaport ? (r.shipLergutB ?? null) : null,
      psSum: Object.keys(byDayPs).length > 0 ? (byDayPs[displayDateStr] ?? null) : null,
      daysSincePs: Object.keys(byDayPs).length > 0 ? getDaysSinceLastPs(byDayPs, displayDateStr) : null,
      inbound: hasRaport ? (r.inbound ?? null) : null,
      inboundA: hasRaport ? (r.inboundA ?? null) : null,
      inboundB: hasRaport ? (r.inboundB ?? null) : null,
      inboundHourlyA: r.inboundA != null ? Math.round((r.inboundA / HOURS_PER_SHIFT) * 100) / 100 : null,
      inboundHourlyB: r.inboundB != null ? Math.round((r.inboundB / HOURS_PER_SHIFT) * 100) / 100 : null,
      powyzej24hRazem: dashboardDataRef.current?.powyzej24h?.razem ?? null,
      powyzej24hKlt: dashboardDataRef.current?.powyzej24h?.klt ?? null,
      powyzej24hGlt: dashboardDataRef.current?.powyzej24h?.glt ?? null,
      najstarszaNieprzyjetaDni: (() => {
        const d = dashboardDataRef.current?.powyzej24h?.najstarszaData;
        if (!d) return null;
        const date = d instanceof Date ? d : new Date(d);
        return Math.floor((Date.now() - date.getTime()) / 86400000);
      })(),
      nieprzyjete902: dashboardDataRef.current?.powyzej24h?.mag902 ?? null,
      nieprzyjete923: dashboardDataRef.current?.powyzej24h?.mag923 ?? null,
      inspektCount: (() => {
        const inspektData = dashboardDataRef.current?.inspektData;
        if (!inspektData?.length) return null;
        const h = inspektData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
        const rows = inspektData.slice(1);
        const dayRows = filterPsRowsByDate(rows, h, displayDateStr);
        return dayRows.length;
      })(),
    });
    if (psRows.length > 0 && psHeaders.length > 0) {
      const dayRows = filterPsRowsByDate(psRows, psHeaders, displayDateStr);
      setPsTable({ headers: psHeaders, rows: dayRows });
    } else {
      setPsTable({ headers: [], rows: [] });
    }
    const prevStr = getPrevDayStr(displayDateStr);
    const prevPick = prevStr && byDayRaport[prevStr] ? byDayRaport[prevStr].pick : null;
    const dodPct = r.pick != null && prevPick != null ? computeDodPct(r.pick, prevPick) : null;
    const pickHourlyA = r.pickA != null ? Math.round((r.pickA / 7) * 100) / 100 : null;
    const pickHourlyB = r.pickB != null ? Math.round((r.pickB / 7) * 100) / 100 : null;
    let bestShiftName = null, bestShiftHourly = null;
    if (pickHourlyA != null && pickHourlyB != null) {
      bestShiftName = pickHourlyA >= pickHourlyB ? 'A' : 'B';
      bestShiftHourly = pickHourlyA >= pickHourlyB ? pickHourlyA : pickHourlyB;
    }
    const from7 = getDateStrOffset(displayDateStr, -6);
    const psRowsLast7 = filterPsRowsByDateRange(psRows, psHeaders, from7, displayDateStr);
    const top3Psa = getTop3PsaFromRows(psRowsLast7, psHeaders);
    const mostFrequentCode = getMostFrequentCodeFromRows(psRowsLast7, psHeaders);
    setTrendPick14(getTrendPickOww14(byDayRaport, displayDateStr));
    setTransportTrend14(getTransportTrend14(byDayRaport, displayDateStr));
    setInboundTrend14(getInboundTrend14(byDayRaport, displayDateStr));
    setInsights({ dodPct, bestShiftName, bestShiftHourly, daysSinceBhp: hasRaport ? getDaysSinceLastBhpEvent(byDayRaport, displayDateStr) : null, top3Psa, mostFrequentCode, alertCount: null });
  }, []);

  const loadDashboard = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const raportSheet = list.find((s) => String(s.name || '').trim() === 'Raport');
      const psSheet = list.find((s) => String(s.name || '').trim() === 'PS');
      const powyzejSheet = list.find((s) => String(s.name || '').trim() === 'POWYŻEJ24H');
      const pojemnikiSheet = list.find((s) => String(s.name || '').trim() === 'POJEMNIKI');
      const inspektSheet = list.find((s) => String(s.name || '').trim().toUpperCase() === 'INSPEKT');

      const raportData = raportSheet ? normalizeSheetData(raportSheet.data) : [];
      const psData = psSheet ? normalizeSheetData(psSheet.data) : [];
      const powyzejData = powyzejSheet?.data ?? [];
      const pojemnikiData = pojemnikiSheet?.data ?? [];
      const inspektData = inspektSheet ? normalizeSheetData(inspektSheet.data) : [];
      const raportHeaders = raportData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const psHeaders = psData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const raportRows = raportData.slice(1);
      const psRows = psData.slice(1);

      const byDayRaport = aggregateRaportByDay(raportRows, raportHeaders);
      const byDayPs = aggregatePsByDay(psRows, psHeaders);
      const raportDates = Object.keys(byDayRaport).sort();
      const psDates = Object.keys(byDayPs).sort();
      const lastRaport = raportDates.length ? raportDates[raportDates.length - 1] : null;
      const lastPs = psDates.length ? psDates[psDates.length - 1] : null;
      const dates = [lastRaport, lastPs].filter(Boolean);
      const displayDateStr = dates.length ? dates.sort()[dates.length - 1] : null;
      const datesList = Array.from(new Set([...raportDates, ...psDates])).sort();
      const powyzej24h = computePowyzej24hKltGlt(powyzejData, pojemnikiData);
      dashboardDataRef.current = { byDayRaport, byDayPs, psRows, psHeaders, raportHeaders, powyzej24h, powyzejData, pojemnikiData, inspektData };
      setAvailableDates(datesList);
      setDisplayDate(displayDateStr);
      setLastRefresh(new Date());
      applyDisplayDate(displayDateStr, byDayRaport, byDayPs, psRows, psHeaders);

    } catch (err) {
      console.error('Błąd ładowania dashboardu KPI:', err);
      setError('Nie udało się załadować danych raportów.');
      setDisplayDate(null);
      setAvailableDates([]);
      setSummary({
        bhp: null, daysSinceBhp: null, pick: null, pickA: null, pickB: null,
        pickHourlyA: null, pickHourlyB: null, repPick: null, repPickA: null, repPickB: null,
        owwHourlyA: null, owwHourlyB: null,
        shipSqA: null, shipSqB: null, shipRepA: null, shipRepB: null, shipLergutA: null, shipLergutB: null,
        psSum: null, daysSincePs: null, inbound: null, inboundA: null, inboundB: null, inboundHourlyA: null, inboundHourlyB: null,
      powyzej24hRazem: null, powyzej24hKlt: null, powyzej24hGlt: null, najstarszaNieprzyjetaDni: null, nieprzyjete902: null, nieprzyjete923: null, inspektCount: null,
    });
      setPsTable({ headers: [], rows: [] });
      setLastRefresh(null);
      setTrendPick14([]);
      setTransportTrend14([]);
      setInboundTrend14([]);
      setInsights({ dodPct: null, bestShiftName: null, bestShiftHourly: null, daysSinceBhp: null, top3Psa: [], mostFrequentCode: null, alertCount: null });
    } finally {
      setLoading(false);
    }
  }, [applyDisplayDate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const { pickYDomain, pickYAxisTicks } = useMemo(() => {
    const min = 3500;
    const step = 3000;
    if (!trendPick14.length) return { pickYDomain: [min, 9500], pickYAxisTicks: [3500, 6500, 9500] };
    const dataMax = Math.max(...trendPick14.map((d) => d.pick), min);
    const yMax = dataMax + step;
    const ticks = [];
    for (let v = min; v <= yMax; v += step) ticks.push(v);
    return { pickYDomain: [min, yMax], pickYAxisTicks: ticks.length ? ticks : [min, min + step] };
  }, [trendPick14]);

  const { replenishmentYDomain, replenishmentYAxisTicks } = useMemo(() => {
    const min = 0;
    const step = 80;
    if (!trendPick14.length) return { replenishmentYDomain: [0, 260], replenishmentYAxisTicks: [0, 100, 180, 260] };
    const dataMax = Math.max(...trendPick14.map((d) => d.oww), 0);
    const yMax = Math.max(dataMax + step, 80);
    const ticks = [];
    for (let v = min; v <= yMax; v += step) ticks.push(v);
    return { replenishmentYDomain: [min, yMax], replenishmentYAxisTicks: ticks.length ? ticks : [0, yMax] };
  }, [trendPick14]);

  const drawerChartData = useMemo(() => {
    if (drawerKpi !== 'PS' || !displayDate) return [];
    const { byDayPs } = dashboardDataRef.current || {};
    return getPsTrend14(byDayPs, displayDate);
  }, [drawerKpi, displayDate]);

  const drawerTitle = useMemo(() => {
    const titles = {
      BHP: 'Szczegóły BHP – trend 14 dni',
      PS: 'Szczegóły PS – trend 14 dni',
      PICK: 'Szczegóły PICK – trend 14 dni',
      REPLENISHMENT: 'Szczegóły REPLENISHMENT – trend 14 dni',
      Transport: 'Szczegóły Transport – trend 14 dni',
      ZMAGAZYNOWANE: 'Szczegóły ZMAGAZYNOWANE – trend 14 dni',
      Powyżej24h: 'Brak przyjęcia Powyżej 24h',
      Najstarszy: 'Najstarszy nieprzyjęty od',
      Inspekt: 'Lista wpisów INSPEKT',
    };
    return titles[drawerKpi] || drawerKpi;
  }, [drawerKpi]);

  const { powyzej24hFullData, najstarszyFullData, inspektFullData } = useMemo(() => {
    const { powyzejData, pojemnikiData, inspektData } = dashboardDataRef.current || {};
    const inspektRes = { headers: [], rows: [] };
    if (inspektData?.length >= 2 && displayDate) {
      const headers = inspektData[0]?.map((c, i) => String((c && typeof c === 'object' ? c.value : c) ?? '').trim() || `Kolumna${i + 1}`) ?? [];
      const dataRows = inspektData.slice(1);
      const dayRows = filterPsRowsByDate(dataRows, headers, displayDate);
      inspektRes.headers = headers;
      inspektRes.rows = dayRows.map((row) => {
        const rec = {};
        headers.forEach((h, i) => {
          const cell = row[i];
          const val = cell && typeof cell === 'object' && 'value' in cell ? cell.value : cell;
          rec[h || `Kolumna${i + 1}`] = val != null ? String(val) : '';
        });
        return rec;
      });
    }
    return {
      powyzej24hFullData: getPowyzej24hFullRows(powyzejData, pojemnikiData, false),
      najstarszyFullData: getPowyzej24hFullRows(powyzejData, pojemnikiData, true, true),
      inspektFullData: inspektRes,
    };
  }, [lastRefresh, displayDate]);

  const handleNavigate = (view) => (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('navigate', { detail: view }));
  };

  if (loading) {
    return (
      <div className="kpi-dashboard kpi-dashboard-tremor">
        <div className="kpi-dashboard-header">
          <Title className="kpi-dashboard-page-title">Strona główna KPI</Title>
          <Text className="kpi-dashboard-page-subtitle">Ładowanie…</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-dashboard kpi-dashboard-tremor">
        <div className="kpi-dashboard-header">
          <Title className="kpi-dashboard-page-title">Strona główna KPI</Title>
          <Text className="kpi-dashboard-page-subtitle kpi-dashboard-error">{error}</Text>
        </div>
      </div>
    );
  }

  const fmt = (v) => (v != null ? String(v) : '—');
  const fmtNum = (v) => (v != null ? String(Math.round(v)) : '—');
  /** Zawsze zwraca string – zapobiega React error #130 (Objects are not valid as React child) */
  const safeRender = (v) => {
    if (v == null) return '';
    if (typeof v === 'object') return v instanceof Date ? formatDateDisplay(formatDateForInput(v)) : String(v);
    return String(v);
  };
  const statusBhp = getStatusDot('bhp', summary.bhp, null);
  const statusPick = getStatusDot('pick', summary.pick, null);
  const statusOww = getStatusDot('oww', summary.repPick, null);
  const statusTransport = getStatusDot('transport', null, null);
  const statusPowyzej24h = getStatusDot('powyzej24h', summary.powyzej24hRazem, null);
  const statusNajstarszy = getStatusDot('najstarszy', summary.najstarszaNieprzyjetaDni, null);
  const nieprzyjeteRazem = summary.nieprzyjete902 != null || summary.nieprzyjete923 != null
    ? (Number(summary.nieprzyjete902) || 0) + (Number(summary.nieprzyjete923) || 0)
    : null;
  const statusNieprzyjete = getStatusDot('nieprzyjete', nieprzyjeteRazem, null);
  const statusInspekt = getStatusDot('inspekt', summary.inspektCount, null);
  const statusInbound = getStatusDot('inbound', summary.inbound, null);
  const statusPs = getStatusDot('ps', summary.psSum, { psTableRows: psTable.rows?.length ?? 0 });

  return (
    <div className="kpi-dashboard kpi-dashboard-tremor kpi-dashboard-home">
      {!displayDate ? (
        <div className="kpi-dashboard-header">
          <Title className="kpi-dashboard-page-title">Strona główna KPI</Title>
          <Text className="kpi-dashboard-no-data">
            Dodaj arkusz „Raport” lub „PS” w Uzupełnij raport i wgraj dane, aby zobaczyć podsumowanie dnia.
          </Text>
        </div>
      ) : (
        <>
          <div ref={printscreenRef} className="kpi-dashboard-print-area">
          <header className="kpi-dashboard-header kpi-dashboard-home-header">
            <div className="kpi-dashboard-report-title-row kpi-dashboard-report-title-row-three">
              <div className="kpi-dashboard-report-title-left">
                <img src={logoMcg} alt="MCG" className="kpi-dashboard-header-logo" />
                <img src={logoMan} alt="MAN" className="kpi-dashboard-header-logo" />
              </div>
              <div className="kpi-dashboard-report-title-center">
                <Title className="kpi-dashboard-page-title">Podsumowanie dnia</Title>
                <span className="kpi-dashboard-report-date">{formatDateDisplay(displayDate)}</span>
              </div>
              <div className="kpi-dashboard-report-title-right">
                <label className="kpi-dashboard-date-picker-wrap">
                  <input
                    type="date"
                    className="kpi-dashboard-date-picker"
                    value={displayDate || ''}
                    min={availableDates[0] || undefined}
                    max={availableDates[availableDates.length - 1] || undefined}
                    onChange={(e) => {
                      const v = e.target.value || null;
                      setDisplayDate(v);
                      if (v && dashboardDataRef.current.byDayRaport) {
                        applyDisplayDate(v, dashboardDataRef.current.byDayRaport, dashboardDataRef.current.byDayPs, dashboardDataRef.current.psRows, dashboardDataRef.current.psHeaders);
                      }
                    }}
                  />
                  <span className="kpi-dashboard-date-picker-label">Wybierz dzień</span>
                </label>
                <button
                  type="button"
                  className="kpi-dashboard-printscreen-btn"
                  onClick={handlePrintscreen}
                  disabled={printscreenBusy}
                  title="Zapisz jako PDF"
                  aria-label="Zapisz jako PDF"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </button>
              </div>
            </div>
          </header>

          <Grid numItemsSm={1} numItemsMd={6} numItemsLg={6} className="kpi-home-cards">
            {/* 1. BHP */}
            <Card
              className={`kpi-home-card kpi-home-card-bhp kpi-home-card-glass kpi-home-card-glass-${statusBhp === 'gray' ? 'green' : statusBhp}`}
            >
              <div className="kpi-home-card-bhp-inner">
                <div className="kpi-home-card-bhp-left">
                  <span className="kpi-home-card-label-wrap">
                    <span className={`kpi-home-status-dot kpi-home-status-dot-${statusBhp}`} aria-hidden />
                    <Text className="kpi-home-card-label">BHP</Text>
                  </span>
                  <Metric className="kpi-home-metric">{fmtNum(summary.bhp)}</Metric>
                </div>
                <div className="kpi-home-badge kpi-home-badge-days">
                  <span className="kpi-home-badge-label">Dni bez zdarzenia</span>
                  <span className="kpi-home-badge-days-value">{fmt(summary.daysSinceBhp)}</span>
                </div>
              </div>
            </Card>

            {/* 2. PS */}
            <Card
              className={`kpi-home-card kpi-home-card-ps kpi-home-card-glass kpi-home-card-glass-${statusPs === 'gray' ? 'green' : statusPs} kpi-podsumowanie-card-clickable${drawerKpi === 'PS' ? ' kpi-podsumowanie-card-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openDrawer('PS')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openDrawer('PS'))}
            >
              <div className="kpi-home-card-bhp-inner">
                <div className="kpi-home-card-bhp-left">
                  <span className="kpi-home-card-label-wrap">
                    <span className={`kpi-home-status-dot kpi-home-status-dot-${statusPs}`} aria-hidden />
                    <Text className="kpi-home-card-label">PS</Text>
                  </span>
                  <Metric className="kpi-home-metric">{fmtNum(summary.psSum)}</Metric>
                </div>
                <div className="kpi-home-badge kpi-home-badge-days">
                  <span className="kpi-home-badge-label">Dni bez PS</span>
                  <span className="kpi-home-badge-days-value">{fmt(summary.daysSincePs)}</span>
                </div>
              </div>
            </Card>

            {/* 3. PICK */}
            <Card
              className="kpi-home-card kpi-home-card-pick"
            >
              <div className="kpi-home-card-ab-inner">
                <div className="kpi-home-card-ab-left">
                  <span className="kpi-home-card-label-wrap">
                    <span className={`kpi-home-status-dot kpi-home-status-dot-${statusPick}`} aria-hidden />
                    <Text className="kpi-home-card-label">PICK</Text>
                  </span>
                  <Metric className="kpi-home-metric">{fmtNum(summary.pick)}</Metric>
                  <div className="kpi-home-hourly-inline">Śr. A: {fmt(summary.pickHourlyA)}/h · Śr. B: {fmt(summary.pickHourlyB)}/h</div>
                </div>
                <div className="kpi-home-card-ab-block kpi-home-card-ab-glass">
                  <div className="kpi-home-card-ab-line">A: {fmtNum(summary.pickA)}</div>
                  <div className="kpi-home-card-ab-line">B: {fmtNum(summary.pickB)}</div>
                </div>
              </div>
            </Card>

            {/* 4. REPLENISHMENT */}
            <Card
              className="kpi-home-card kpi-home-card-oww"
            >
              <div className="kpi-home-card-ab-inner">
                <div className="kpi-home-card-ab-left">
                  <span className="kpi-home-card-label-wrap">
                    <span className={`kpi-home-status-dot kpi-home-status-dot-${statusOww}`} aria-hidden />
                    <Text className="kpi-home-card-label">REPLENISHMENT</Text>
                  </span>
                  <Metric className="kpi-home-metric">{fmtNum(summary.repPick)}</Metric>
                  <div className="kpi-home-hourly-inline">Śr. A: {fmt(summary.owwHourlyA)}/h · Śr. B: {fmt(summary.owwHourlyB)}/h</div>
                </div>
                <div className="kpi-home-card-ab-block kpi-home-card-ab-glass">
                  <div className="kpi-home-card-ab-line">A: {fmtNum(summary.repPickA)}</div>
                  <div className="kpi-home-card-ab-line">B: {fmtNum(summary.repPickB)}</div>
                </div>
              </div>
            </Card>

            {/* 5. Transport */}
            <Card
              className="kpi-home-card kpi-home-card-transport"
            >
              <span className="kpi-home-card-label-wrap">
                <span className={`kpi-home-status-dot kpi-home-status-dot-${statusTransport}`} aria-hidden />
                <Text className="kpi-home-card-label">Transport</Text>
              </span>
              <Metric className="kpi-home-metric">{fmtNum((summary.shipSqA ?? 0) + (summary.shipSqB ?? 0) + (summary.shipRepA ?? 0) + (summary.shipRepB ?? 0) + (summary.shipLergutA ?? 0) + (summary.shipLergutB ?? 0))}</Metric>
              <div className="kpi-home-shift-inline">SQ: {fmtNum((summary.shipSqA ?? 0) + (summary.shipSqB ?? 0))} · REP: {fmtNum((summary.shipRepA ?? 0) + (summary.shipRepB ?? 0))} · LERGUT: {fmtNum((summary.shipLergutA ?? 0) + (summary.shipLergutB ?? 0))}</div>
            </Card>

            {/* 6. ZMAGAZYNOWANE (INBOUND z Raport) */}
            <Card
              className="kpi-home-card kpi-home-card-inbound"
            >
              <div className="kpi-home-card-ab-inner">
                <div className="kpi-home-card-ab-left">
                  <span className="kpi-home-card-label-wrap">
                    <span className={`kpi-home-status-dot kpi-home-status-dot-${statusInbound}`} aria-hidden />
                    <Text className="kpi-home-card-label">ZMAGAZYNOWANE</Text>
                  </span>
                  <Metric className="kpi-home-metric">{fmtNum(summary.inbound)}</Metric>
                  <div className="kpi-home-hourly-inline">Śr. A: {fmt(summary.inboundHourlyA)}/h · Śr. B: {fmt(summary.inboundHourlyB)}/h</div>
                </div>
                <div className="kpi-home-card-ab-block kpi-home-card-ab-glass">
                  <div className="kpi-home-card-ab-line">A: {fmtNum(summary.inboundA)}</div>
                  <div className="kpi-home-card-ab-line">B: {fmtNum(summary.inboundB)}</div>
                </div>
              </div>
            </Card>

            {/* 7. Powyżej 24h */}
            <Card
              className={`kpi-home-card kpi-home-card-powyzej24h kpi-home-card-glass kpi-home-card-glass-${statusPowyzej24h === 'gray' ? 'green' : statusPowyzej24h} kpi-podsumowanie-card-clickable${drawerKpi === 'Powyżej24h' ? ' kpi-podsumowanie-card-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openDrawer('Powyżej24h')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openDrawer('Powyżej24h'))}
            >
              <span className="kpi-home-card-label-wrap">
                <span className={`kpi-home-status-dot kpi-home-status-dot-${statusPowyzej24h}`} aria-hidden />
                <Text className="kpi-home-card-label">Brak przyjęcia Powyżej 24h:</Text>
              </span>
              <Metric className="kpi-home-metric">{fmtNum(summary.powyzej24hRazem)}</Metric>
              {(summary.powyzej24hRazem == null && summary.powyzej24hKlt == null) && (
                <Text className="kpi-home-ps-sztuki" style={{ marginBottom: 4 }}>Brak danych (POWYŻEJ24H)</Text>
              )}
              <div className="kpi-home-shift-inline">KLT: {fmtNum(summary.powyzej24hKlt)} · GLT: {fmtNum(summary.powyzej24hGlt)}</div>
            </Card>

            {/* 8. Najstarszy nieprzyjęty od */}
            <Card
              className={`kpi-home-card kpi-home-card-najstarszy kpi-home-card-glass kpi-home-card-glass-${statusNajstarszy === 'gray' ? 'green' : statusNajstarszy} kpi-podsumowanie-card-clickable${drawerKpi === 'Najstarszy' ? ' kpi-podsumowanie-card-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openDrawer('Najstarszy')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openDrawer('Najstarszy'))}
            >
              <span className="kpi-home-card-label-wrap">
                <span className={`kpi-home-status-dot kpi-home-status-dot-${statusNajstarszy}`} aria-hidden />
                <Text className="kpi-home-card-label">Najstarszy nieprzyjęty od:</Text>
              </span>
              <Metric className="kpi-home-metric">{summary.najstarszaNieprzyjetaDni != null ? fmtNum(summary.najstarszaNieprzyjetaDni) : '—'}</Metric>
              <Text className="kpi-home-ps-sztuki">dni</Text>
            </Card>

            {/* NIEPRZYJĘTE – ostatnie wklejenie POWYŻEJ24H: magazyn 902 / 923 */}
            <Card
              className={`kpi-home-card kpi-home-card-nieprzyjete kpi-home-card-glass kpi-home-card-glass-${statusNieprzyjete === 'gray' ? 'green' : statusNieprzyjete}`}
            >
              <span className="kpi-home-card-label-wrap">
                <span className={`kpi-home-status-dot kpi-home-status-dot-${statusNieprzyjete}`} aria-hidden />
                <Text className="kpi-home-card-label">NIEPRZYJĘTE</Text>
              </span>
              <div className="kpi-home-badge kpi-home-badge-days kpi-home-card-nieprzyjete-mag">
                <div className="kpi-home-card-ab-line">902 – Przyjęcie: {fmtNum(summary.nieprzyjete902)}</div>
                <div className="kpi-home-card-ab-line">923 – Przepak: {fmtNum(summary.nieprzyjete923)}</div>
              </div>
            </Card>

            {/* 9. INSPEKT – pod PICK, obok Najstarszy */}
            <Card
              className={`kpi-home-card kpi-home-card-inspekt kpi-home-card-glass kpi-home-card-glass-${statusInspekt === 'gray' ? 'green' : statusInspekt} kpi-podsumowanie-card-clickable${drawerKpi === 'Inspekt' ? ' kpi-podsumowanie-card-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openDrawer('Inspekt')}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openDrawer('Inspekt'))}
            >
              <span className="kpi-home-card-label-wrap">
                <span className={`kpi-home-status-dot kpi-home-status-dot-${statusInspekt}`} aria-hidden />
                <Text className="kpi-home-card-label">INSPEKT</Text>
              </span>
              <Metric className="kpi-home-metric">{summary.inspektCount != null ? fmtNum(summary.inspektCount) : '—'}</Metric>
              <Text className="kpi-home-ps-sztuki">wpisów</Text>
            </Card>

          </Grid>

          {/* Moduł z informacjami – po kliknięciu kafelka KPI */}
          {drawerOpen && (
            <div ref={chartPanelRef} className="kpi-podsumowanie-chart-panel kpi-dashboard-drawer-overlay">
              <div className="kpi-podsumowanie-chart-panel-header">
                <Title className="kpi-podsumowanie-chart-panel-title">{drawerTitle}</Title>
                <button type="button" className="kpi-podsumowanie-chart-panel-close" onClick={closeDrawer} aria-label="Zamknij">✕</button>
              </div>
              <div className="kpi-podsumowanie-chart-panel-body">
                {drawerKpi === 'Powyżej24h' ? (
                  <Card className="kpi-podsumowanie-chart-panel-card">
                    <div className="kpi-dashboard-drawer-overlay-inner">
                      <div className="kpi-powyzej24h-table-wrap kpi-home-table-wrap" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                        <Table className="kpi-home-table">
                          <TableHead>
                            <TableRow>
                              {powyzej24hFullData.headers.map((h, i) => (
                                <TableHeaderCell key={i}>{safeRender(h) || `Kolumna ${i + 1}`}</TableHeaderCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {powyzej24hFullData.rows.map((row, idx) => (
                              <TableRow key={idx}>
                                {powyzej24hFullData.headers.map((h, c) => (
                                  <TableCell key={c}>{safeRender(row[h])}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </Card>
                ) : drawerKpi === 'Najstarszy' ? (
                  <Card className="kpi-podsumowanie-chart-panel-card">
                    <div className="kpi-dashboard-drawer-overlay-inner">
                      <div className="kpi-najstarszy-table-wrap kpi-home-table-wrap" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                        <Table className="kpi-home-table">
                          <TableHead>
                            <TableRow>
                              {najstarszyFullData.headers.map((h, i) => (
                                <TableHeaderCell key={i}>{safeRender(h) || `Kolumna ${i + 1}`}</TableHeaderCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {najstarszyFullData.rows.map((row, idx) => (
                              <TableRow key={idx}>
                                {najstarszyFullData.headers.map((h, c) => (
                                  <TableCell key={c}>{safeRender(row[h])}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </Card>
                ) : drawerKpi === 'Inspekt' ? (
                  <Card className="kpi-podsumowanie-chart-panel-card">
                    <div className="kpi-dashboard-drawer-overlay-inner">
                      <Text className="kpi-podsumowanie-chart-panel-label">Lista wpisów INSPEKT – za dzień {displayDate ? formatDateDisplay(displayDate) : ''}</Text>
                      {inspektFullData.headers.length === 0 ? (
                        <Text className="kpi-dashboard-chart-empty">Brak arkusza „INSPEKT” w Uzupełnij raport. Dodaj arkusz o nazwie INSPEKT.</Text>
                      ) : (
                      <div className="kpi-inspekt-table-wrap kpi-home-table-wrap" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                        <Table className="kpi-home-table">
                          <TableHead>
                            <TableRow>
                              {inspektFullData.headers.map((h, i) => (
                                <TableHeaderCell key={i}>{safeRender(h) || `Kolumna ${i + 1}`}</TableHeaderCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {inspektFullData.rows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={inspektFullData.headers.length || 1} className="kpi-ps-table-empty-cell">
                                  Brak wpisów INSPEKT za wybrany dzień.
                                </TableCell>
                              </TableRow>
                            ) : (
                              inspektFullData.rows.map((row, idx) => (
                                <TableRow key={idx}>
                                  {inspektFullData.headers.map((h, c) => (
                                    <TableCell key={c}>{safeRender(row[h])}</TableCell>
                                  ))}
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      )}
                    </div>
                  </Card>
                ) : drawerKpi === 'PS' && drawerChartData.length > 0 ? (
                  <Card className="kpi-podsumowanie-chart-panel-card">
                    <div className="kpi-dashboard-drawer-overlay-inner">
                      <Text className="kpi-podsumowanie-chart-panel-label">Trend 14 dni – {displayDate ? formatDateDisplay(displayDate) : ''}</Text>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={drawerChartData} margin={{ top: 28, right: 16, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                          <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                          <Tooltip contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 8 }} labelStyle={{ color: '#e2e8f0' }} />
                          <Bar dataKey="value" name="PS" fill="#42a5f5" radius={[4, 4, 0, 0]}>
                            <LabelList dataKey="value" position="top" fill="#e2e8f0" fontSize={11} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                ) : (
                  <Card className="kpi-podsumowanie-chart-panel-card">
                    <Text className="kpi-dashboard-chart-empty">Brak danych do wyświetlenia.</Text>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Trend 14 dni PICK + Miks transportu */}
          <Grid numItemsSm={1} numItemsLg={2} className="kpi-home-charts-row">
            <Card className="kpi-home-chart-card">
              <Title className="kpi-home-chart-title">Trend 14 dni: PICK</Title>
              {trendPick14.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={trendPick14} margin={{ top: 36, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                      <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis domain={pickYDomain} ticks={pickYAxisTicks} tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} allowDataOverflow />
                      <Tooltip
                        contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 8 }}
                        labelStyle={{ color: '#e2e8f0' }}
                        labelFormatter={(label) => `Dzień ${label}`}
                      />
                      <Line type="monotone" dataKey="pick" name="PICK" stroke="#00bcd4" strokeWidth={2} dot={{ fill: '#00bcd4', r: 3 }}>
                        <LabelList dataKey="pick" position="top" content={(p) => <ChartValueLabel {...p} dataKey="pick" color="#00bcd4" />} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <Text className="kpi-dashboard-chart-empty">Brak danych do wykresu.</Text>
              )}
            </Card>
            <Card className="kpi-home-chart-card">
              <div className="kpi-home-chart-header-with-legend" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
                <Title className="kpi-home-chart-title" style={{ margin: 0 }}>Trend 14 dni: SQ / REP / LERGUT</Title>
                <div className="kpi-home-chart-legend-inline" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 2, background: '#66bb6a' }} /><span style={{ color: '#94a3b8', fontSize: 11 }}>LERGUT</span></span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 2, background: '#ff9800' }} /><span style={{ color: '#94a3b8', fontSize: 11 }}>REP</span></span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 2, background: '#42a5f5' }} /><span style={{ color: '#94a3b8', fontSize: 11 }}>SQ</span></span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 8, borderBottom: '2px dashed #94a3b8', boxSizing: 'border-box' }} /><span style={{ color: '#94a3b8', fontSize: 11 }}>SUMA = SQ+REP+LERGUT</span></span>
                </div>
              </div>
              {transportTrend14.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={transportTrend14} margin={{ top: 36, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 8 }}
                      labelStyle={{ color: '#e2e8f0' }}
                      labelFormatter={(label) => `Dzień ${label}`}
                    />
                    <Line type="monotone" dataKey="sq" name="SQ" stroke="#42a5f5" strokeWidth={2} dot={{ fill: '#42a5f5', r: 3 }} />
                    <Line type="monotone" dataKey="rep" name="REP" stroke="#ff9800" strokeWidth={2} dot={{ fill: '#ff9800', r: 3 }} />
                    <Line type="monotone" dataKey="lergut" name="LERGUT" stroke="#66bb6a" strokeWidth={2} dot={{ fill: '#66bb6a', r: 3 }} />
                    <Line type="monotone" dataKey="razem" name="SUMA = SQ+REP+LERGUT" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#94a3b8', r: 3 }}>
                      <LabelList dataKey="razem" position="top" content={(p) => <ChartValueLabel {...p} dataKey="razem" color="#94a3b8" />} />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text className="kpi-dashboard-chart-empty">Brak danych do wykresu.</Text>
              )}
            </Card>
          </Grid>

          {/* Trend 14 dni: ZMAGAZYNOWANE (słupki) + Trend 14 dni Replenishment */}
          <div className="kpi-home-insights-row">
            <Card className="kpi-home-chart-card kpi-home-inbound-chart-card">
              <Title className="kpi-home-chart-title">Trend 14 dni: ZMAGAZYNOWANE</Title>
              {inboundTrend14.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={inboundTrend14} margin={{ top: 28, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 8 }}
                      labelStyle={{ color: '#e2e8f0' }}
                      labelFormatter={(label) => `Dzień ${label}`}
                      formatter={(value) => [value, 'ZMAGAZYNOWANE']}
                      cursor={{ fill: 'rgba(96, 125, 139, 0.35)' }}
                    />
                    <Bar dataKey="inbound" name="ZMAGAZYNOWANE" fill="#607d8b" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="inbound" position="top" fill="#e2e8f0" fontSize={11} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Text className="kpi-dashboard-chart-empty">Brak danych do wykresu.</Text>
              )}
            </Card>
            <Card className="kpi-home-chart-card kpi-home-oww-chart-card">
              <Title className="kpi-home-chart-title">Trend 14 dni: Replenishment</Title>
              {trendPick14.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendPick14} margin={{ top: 36, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis domain={replenishmentYDomain} ticks={replenishmentYAxisTicks} tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} allowDataOverflow />
                    <Tooltip
                      contentStyle={{ background: '#1e2433', border: '1px solid #334155', borderRadius: 8 }}
                      labelStyle={{ color: '#e2e8f0' }}
                      labelFormatter={(label) => `Dzień ${label}`}
                    />
                    <Line type="monotone" dataKey="oww" name="Replenishment" stroke="#d4a057" strokeWidth={2} dot={{ fill: '#d4a057', r: 3 }}>
                      <LabelList dataKey="oww" position="top" content={(p) => <ChartValueLabel {...p} dataKey="oww" color="#d4a057" />} />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text className="kpi-dashboard-chart-empty">Brak danych do wykresu.</Text>
              )}
            </Card>
          </div>

          {/* Błędy / PS – lista */}
          <Card className="kpi-home-errors-card">
            <Title className="kpi-home-errors-title">Błędy / PS – lista</Title>
            {psTable.headers.length === 0 ? (
              <Text className="kpi-dashboard-chart-empty">Brak arkusza PS lub brak danych.</Text>
            ) : (
              <div className="kpi-home-table-wrap">
                {(!psTable.rows || psTable.rows.length === 0) ? (
                  <Text className="kpi-dashboard-chart-empty">Brak wpisów za dzień {displayDate}.</Text>
                ) : (
                  <Table className="kpi-home-table">
                    <TableHead>
                      <TableRow>
                        {psTable.headers.map((h, i) => (
                          <TableHeaderCell key={i}>{safeRender(typeof h === 'object' ? h?.value : h) || `Kolumna ${i + 1}`}</TableHeaderCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {psTable.rows.map((row, idx) => (
                        <TableRow key={idx}>
                          {row.map((cell, c) => (
                            <TableCell key={c}>{safeRender(cell && typeof cell === 'object' ? cell.value : cell)}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Card>
          </div>
        </>
      )}
    </div>
  );
}

export default KPIDashboard;
